import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  TextField,
  InputAdornment,
  IconButton,
  Chip,
  Typography,
  Fab,
  Menu,
  MenuItem,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Avatar,
  Stack,
  Badge,
  Divider,
  Button,
} from '@mui/material';
import {
  Search,
  FilterList,
  Add,
  Person,
  MoreVert,
  Block,
  Edit,
  Visibility,
  Close,
  CheckCircle,
  ErrorOutline,
  FileUpload,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import api from '../../utils/api';
import StudentDetailDialog from './StudentDetailDialog';
import StudentFormDialog from './StudentFormDialog';
import BulkImportDialog from './BulkImportDialog';
import { GRADIENTS, RADIUS } from '../../theme/tokens';

const MotionCard = motion(Card);

/**
 * Student Management Component
 * Mobile-first CRM for librarians to manage students
 */
function StudentManagement({ showSnackbar }) {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filters, setFilters] = useState({
    status: 'all',
    city: '',
    has_active_booking: false,
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    total_pages: 0,
  });

  // Dialogs
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [detailInitialTab, setDetailInitialTab] = useState(0);

  // Filter menu
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);

  // Bulk import
  const [bulkImportOpen, setBulkImportOpen] = useState(false);

  // Student card menu
  const [cardMenuAnchor, setCardMenuAnchor] = useState(null);
  const [menuStudent, setMenuStudent] = useState(null);

  useEffect(() => {
    fetchStudents();
  }, [searchQuery, filters, pagination.page]);

  const fetchStudents = async () => {
    try {
      setLoading(true);

      const params = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
      });

      if (searchQuery) params.append('search', searchQuery);
      if (filters.status !== 'all') params.append('status', filters.status);
      if (filters.city) params.append('city', filters.city);
      if (filters.has_active_booking) params.append('has_active_booking', 'true');

      const response = await api.get(`/librarian/students?${params.toString()}`);

      setStudents(response.data.data.students);
      setPagination(response.data.data.pagination);
    } catch (error) {
      console.error('Error fetching students:', error);
      showSnackbar('Failed to load students', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
    setPagination({ ...pagination, page: 1 }); // Reset to page 1 on search
  };

  const clearSearch = () => {
    setSearchQuery('');
  };

  const toggleFilter = (filterType, value) => {
    setFilters({ ...filters, [filterType]: value });
    setPagination({ ...pagination, page: 1 });
  };

  const openDetailDialog = (student, initialTab = 0) => {
    setSelectedStudent(student);
    setDetailInitialTab(initialTab);
    setDetailDialogOpen(true);
  };

  const openFormDialog = (student = null) => {
    setSelectedStudent(student);
    setEditMode(!!student);
    setFormDialogOpen(true);
  };

  const handleStudentUpdated = (createdStudent = null) => {
    fetchStudents();
    setFormDialogOpen(false);

    // If a new student was created, open detail dialog with payments tab
    if (createdStudent) {
      setSelectedStudent(createdStudent);
      setDetailInitialTab(2); // 2 = Payments tab
      setDetailDialogOpen(true);
    } else {
      setDetailDialogOpen(false);
    }
  };

  const handleCardMenuOpen = (event, student) => {
    setCardMenuAnchor(event.currentTarget);
    setMenuStudent(student);
  };

  const handleCardMenuClose = () => {
    setCardMenuAnchor(null);
    setMenuStudent(null);
  };

  const handleViewDetails = () => {
    openDetailDialog(menuStudent);
    handleCardMenuClose();
  };

  const handleEditStudent = () => {
    openFormDialog(menuStudent);
    handleCardMenuClose();
  };

  const handleBlockStudent = async () => {
    try {
      await api.put(`/librarian/students/${menuStudent.id}/block`, {
        blocked: !menuStudent.blocked,
      });

      showSnackbar(
        menuStudent.blocked ? 'Student unblocked successfully' : 'Student blocked successfully',
        'success'
      );

      fetchStudents();
      handleCardMenuClose();
    } catch (error) {
      console.error('Error blocking student:', error);
      showSnackbar('Failed to update student status', 'error');
    }
  };

  const activeFilterCount = Object.values(filters).filter(
    (v) => v && v !== 'all' && v !== false
  ).length;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.05,
      },
    },
  };

  const cardVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: { y: 0, opacity: 1 },
    exit: { y: -20, opacity: 0 },
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'transparent', pb: 10 }}>
      <Container maxWidth="lg" sx={{ py: 2 }}>
        {/* Header */}
        <Box mb={3} display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="h5" fontWeight="800" gutterBottom>
              Student Management
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Manage students, track payments, and add notes
            </Typography>
          </Box>
          <Button
            variant="outlined"
            size="small"
            startIcon={<FileUpload />}
            onClick={() => setBulkImportOpen(true)}
            sx={{ borderRadius: RADIUS.md, whiteSpace: 'nowrap' }}
          >
            Bulk Import
          </Button>
        </Box>

        {/* Search Bar */}
        <Box mb={2}>
          <TextField
            fullWidth
            placeholder="Search by name, email, or phone..."
            value={searchQuery}
            onChange={handleSearch}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={clearSearch}>
                    <Close />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: RADIUS.lg,
                bgcolor: 'background.paper',
              },
            }}
          />
        </Box>

        {/* Filter Chips */}
        <Box mb={2} display="flex" gap={1} flexWrap="wrap" alignItems="center">
          <IconButton
            size="small"
            onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
            sx={{
              bgcolor: activeFilterCount > 0 ? 'primary.main' : 'action.hover',
              color: activeFilterCount > 0 ? 'white' : 'text.primary',
              '&:hover': {
                bgcolor: activeFilterCount > 0 ? 'primary.dark' : 'action.selected',
              },
            }}
          >
            <Badge badgeContent={activeFilterCount} color="error">
              <FilterList />
            </Badge>
          </IconButton>

          <Chip
            label="All"
            onClick={() => toggleFilter('status', 'all')}
            color={filters.status === 'all' ? 'primary' : 'default'}
            variant={filters.status === 'all' ? 'filled' : 'outlined'}
          />
          <Chip
            label="Active"
            onClick={() => toggleFilter('status', 'active')}
            color={filters.status === 'active' ? 'success' : 'default'}
            variant={filters.status === 'active' ? 'filled' : 'outlined'}
            icon={<CheckCircle />}
          />
          <Chip
            label="Blocked"
            onClick={() => toggleFilter('status', 'blocked')}
            color={filters.status === 'blocked' ? 'error' : 'default'}
            variant={filters.status === 'blocked' ? 'filled' : 'outlined'}
            icon={<Block />}
          />
        </Box>

        {/* Results Count */}
        <Typography variant="body2" color="text.secondary" mb={2}>
          {pagination.total} student{pagination.total !== 1 ? 's' : ''} found
        </Typography>

        {/* Student List */}
        {loading ? (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        ) : students.length === 0 ? (
          <Alert severity="info" sx={{ borderRadius: RADIUS.lg }}>
            {searchQuery
              ? 'No students found matching your search'
              : 'No students yet. Click the + button to add one!'}
          </Alert>
        ) : (
          <motion.div variants={containerVariants} initial="hidden" animate="visible">
            <AnimatePresence>
              {students.map((student) => (
                <MotionCard
                  key={student.id}
                  variants={cardVariants}
                  layout
                  onClick={() => openDetailDialog(student)}
                  sx={{
                    mb: 2,
                    borderRadius: RADIUS.lg,
                    cursor: 'pointer',
                    transition: 'all 0.2s',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent sx={{ p: 2 }}>
                    <Box display="flex" alignItems="flex-start" gap={2}>
                      {/* Avatar */}
                      <Avatar
                        sx={{
                          width: 56,
                          height: 56,
                          bgcolor: student.blocked ? 'error.main' : 'primary.main',
                          fontSize: '1.5rem',
                          fontWeight: 700,
                        }}
                      >
                        {student.name?.charAt(0).toUpperCase()}
                      </Avatar>

                      {/* Info */}
                      <Box flex={1} minWidth={0}>
                        <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                          <Typography
                            variant="subtitle1"
                            fontWeight="700"
                            sx={{
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {student.name}
                          </Typography>
                          {student.blocked && (
                            <Chip label="Blocked" size="small" color="error" />
                          )}
                        </Box>

                        <Stack spacing={0.5}>
                          <Typography variant="body2" color="text.secondary">
                            {student.email}
                          </Typography>
                          {student.phone && (
                            <Typography variant="body2" color="text.secondary">
                              {student.phone}
                            </Typography>
                          )}
                          <Box display="flex" gap={1} flexWrap="wrap">
                            {student.age && (
                              <Chip label={`${student.age} yrs`} size="small" variant="outlined" />
                            )}
                            {student.city && (
                              <Chip label={student.city} size="small" variant="outlined" />
                            )}
                          </Box>
                        </Stack>
                      </Box>

                      {/* Actions Menu */}
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCardMenuOpen(e, student);
                        }}
                      >
                        <MoreVert />
                      </IconButton>
                    </Box>
                  </CardContent>
                </MotionCard>
              ))}
            </AnimatePresence>
          </motion.div>
        )}

        {/* Pagination */}
        {pagination.total_pages > 1 && (
          <Box display="flex" justifyContent="center" gap={1} mt={3}>
            {Array.from({ length: pagination.total_pages }, (_, i) => i + 1).map((page) => (
              <Chip
                key={page}
                label={page}
                onClick={() => setPagination({ ...pagination, page })}
                color={pagination.page === page ? 'primary' : 'default'}
                variant={pagination.page === page ? 'filled' : 'outlined'}
              />
            ))}
          </Box>
        )}
      </Container>

      {/* Floating Action Button - Add Student */}
      <Fab
        color="primary"
        aria-label="add student"
        onClick={() => openFormDialog()}
        sx={{
          position: 'fixed',
          bottom: 80,
          right: 16,
          background: GRADIENTS.brand,
        }}
      >
        <Add />
      </Fab>

      {/* Card Actions Menu */}
      <Menu
        anchorEl={cardMenuAnchor}
        open={Boolean(cardMenuAnchor)}
        onClose={handleCardMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleViewDetails}>
          <Visibility sx={{ mr: 1.5, fontSize: 20 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={handleEditStudent}>
          <Edit sx={{ mr: 1.5, fontSize: 20 }} />
          Edit Student
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleBlockStudent} sx={{ color: 'error.main' }}>
          <Block sx={{ mr: 1.5, fontSize: 20 }} />
          {menuStudent?.blocked ? 'Unblock' : 'Block'} Student
        </MenuItem>
      </Menu>

      {/* Student Detail Dialog */}
      {selectedStudent && (
        <StudentDetailDialog
          open={detailDialogOpen}
          onClose={() => {
            setDetailDialogOpen(false);
            setDetailInitialTab(0); // Reset to default tab
          }}
          student={selectedStudent}
          onStudentUpdated={handleStudentUpdated}
          showSnackbar={showSnackbar}
          initialTab={detailInitialTab}
        />
      )}

      {/* Add/Edit Student Dialog */}
      <StudentFormDialog
        open={formDialogOpen}
        onClose={() => setFormDialogOpen(false)}
        student={editMode ? selectedStudent : null}
        onSuccess={handleStudentUpdated}
        showSnackbar={showSnackbar}
      />

      {/* Bulk Import Dialog */}
      <BulkImportDialog
        open={bulkImportOpen}
        onClose={() => setBulkImportOpen(false)}
        onSuccess={() => fetchStudents()}
        showSnackbar={showSnackbar}
      />
    </Box>
  );
}

export default StudentManagement;
